import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/* ── helpers ── */
function somarCampo(arr, campo) { return arr.reduce((a, d) => a + (Number(d[campo]) || 0), 0); }
function fmtMoeda(v) { return `R$ ${Number(v||0).toLocaleString('pt-BR', { minimumFractionDigits:2 })}`; }
function fmtK(v)     { return v >= 1000 ? `R$${(v/1000).toFixed(1)}k` : `R$${v}`; }
function fmtData(s)  { if (!s) return '—'; const [y,m,d] = s.split('-'); return `${d}/${m}/${y}`; }
function diasAtras(ts) { return ts ? Math.floor((Date.now() - new Date(ts)) / 86400000) : null; }

function exportarCSV(linhas, nome) {
  const csv = linhas.map(r => r.join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type:'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = nome; a.click();
}

/* ── componentes visuais ── */
function KpiGrad({ label, valor, grad, icon, sub }) {
  return (
    <div style={{ background: grad, borderRadius:16, padding:'18px 20px', color:'white', position:'relative', overflow:'hidden', boxShadow:'0 6px 20px rgba(0,0,0,0.13)' }}>
      <div style={{ position:'absolute', top:-15, right:-15, width:70, height:70, background:'rgba(255,255,255,0.08)', borderRadius:'50%' }} />
      <div style={{ fontSize:10, color:'rgba(255,255,255,0.75)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600, marginBottom:6 }}>{icon} {label}</div>
      <div style={{ fontSize:28, fontWeight:700, fontFamily:'Playfair Display,serif', lineHeight:1 }}>{valor}</div>
      {sub && <div style={{ fontSize:10, color:'rgba(255,255,255,0.6)', marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function MiniBar({ valor, max, cor }) {
  const pct = max > 0 ? Math.round((valor / max) * 100) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:7, background:'rgba(107,33,168,0.08)', borderRadius:10, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:cor, borderRadius:10, transition:'width 0.4s' }} />
      </div>
      <span style={{ fontSize:11, fontWeight:700, color:cor, minWidth:28, textAlign:'right' }}>{valor}</span>
    </div>
  );
}

function BarChart({ dados, campo, cor, label, fmt }) {
  const max = Math.max(...dados.map(d => d[campo] || 0), 1);
  return (
    <div>
      <div style={{ fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:12, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</div>
      <div style={{ display:'flex', gap:3, alignItems:'flex-end', height:90, overflowX:'auto' }}>
        {dados.map((d, i) => {
          const h = ((d[campo] || 0) / max) * 90;
          const lbl = d.dia || (d.nome||'').split(' ')[0];
          return (
            <div key={i} style={{ minWidth:28, flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
              <div style={{ fontSize:8, fontWeight:700, color:cor }}>{fmt ? fmt(d[campo]) : (d[campo]||0)}</div>
              <div style={{ width:'100%', background:'rgba(107,33,168,0.08)', borderRadius:'4px 4px 0 0', height:80, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
                <div style={{ width:'100%', height:`${h}px`, background:`linear-gradient(180deg,${cor},${cor}88)`, borderRadius:'4px 4px 0 0', transition:'height 0.4s' }} />
              </div>
              <div style={{ fontSize:7.5, color:'#9ca3af', textAlign:'center', whiteSpace:'nowrap', maxWidth:32, overflow:'hidden', textOverflow:'ellipsis' }} title={lbl}>{lbl}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── página principal ── */
export default function Relatorios() {
  const hoje          = new Date().toISOString().split('T')[0];
  const trintaDias    = new Date(Date.now() - 30*86400000).toISOString().split('T')[0];
  const sessenta      = new Date(Date.now() - 60*86400000).toISOString().split('T')[0];

  /* filtros */
  const [filtroProj,   setFiltroProj]   = useState('');
  const [filtroColab,  setFiltroColab]  = useState('');
  const [tipoPeriodo,  setTipoPeriodo]  = useState('mes');
  const [diaEspec,     setDiaEspec]     = useState(hoje);
  const [dataInicio,   setDataInicio]   = useState(trintaDias);
  const [dataFim,      setDataFim]      = useState(hoje);
  const [comparar,     setComparar]     = useState(false);
  const [compInicio,   setCompInicio]   = useState(sessenta);
  const [compFim,      setCompFim]      = useState(trintaDias);
  const [aba,          setAba]          = useState('geral');

  /* dados */
  const [carregando,   setCarregando]   = useState(false);
  const [linhas,       setLinhas]       = useState([]);
  const [linhasComp,   setLinhasComp]   = useState([]);
  const [casos,        setCasos]        = useState([]);
  const [tarefasData,  setTarefasData]  = useState([]);      // tarefas do período com lead info
  const [etapasSnap,   setEtapasSnap]   = useState([]);
  const [projetosMap,  setProjetosMap]  = useState({});
  const [colabLista,   setColabLista]   = useState([]);
  const [projLista,    setProjLista]    = useState([]);

  useEffect(() => {
    supabase.from('colaboradores').select('nome').order('nome').then(({ data }) => {
      if (data) setColabLista(data.map(c => c.nome));
    });
    supabase.from('projetos').select('id, nome').order('nome').then(({ data }) => {
      if (data) {
        setProjLista(data.map(p => p.nome));
        const m = {};
        data.forEach(p => { m[p.id] = p.nome; });
        setProjetosMap(m);
      }
    });
    // snapshot atual de etapas
    supabase.from('crm_leads').select('etapa, funil_key, projeto, perdido').then(({ data }) => {
      if (data) setEtapasSnap(data);
    });
  }, []);

  /* ── buscar e agregar dados ── */
  const buscarAgregado = useCallback(async (inicio, fim) => {
    const [
      { data: leadsData },
      { data: agendaData },
      { data: finData },
    ] = await Promise.all([
      supabase.from('crm_leads')
        .select('created_at, responsavel, projeto, etapa, funil_key, perdido')
        .gte('created_at', inicio + 'T00:00:00')
        .lte('created_at', fim + 'T23:59:59'),
      supabase.from('agenda')
        .select('data, responsaveis, tipo, projeto')
        .gte('data', inicio).lte('data', fim),
      supabase.from('financeiro')
        .select('data_lancamento, valor, projeto')
        .eq('tipo', 'entrada').eq('status', 'Recebido')
        .gte('data_lancamento', inicio).lte('data_lancamento', fim),
    ]);

    const mapa = {};
    const dia = (ts) => (ts||'').split('T')[0];

    (leadsData || []).forEach(l => {
      const d = dia(l.created_at);
      const key = `${d}|${l.responsavel||''}|${l.projeto||''}`;
      if (!mapa[key]) mapa[key] = { data:d, colaborador:l.responsavel||'', projeto:l.projeto||'', leads:0, perdidos:0, contratos:0, reunioes:0, receita:0, atividades:0 };
      mapa[key].leads++;
      mapa[key].atividades++;
      if (l.perdido) mapa[key].perdidos++;
    });

    (agendaData || []).forEach(e => {
      const isReu = ['call','reuniao','proposta'].includes(e.tipo);
      const resps = e.responsaveis?.length ? e.responsaveis : [''];
      resps.forEach(resp => {
        const key = `${e.data}|${resp}|${e.projeto||''}`;
        if (!mapa[key]) mapa[key] = { data:e.data, colaborador:resp, projeto:e.projeto||'', leads:0, perdidos:0, contratos:0, reunioes:0, receita:0, atividades:0 };
        if (isReu) mapa[key].reunioes++;
        mapa[key].atividades++;
      });
    });

    (finData || []).forEach(f => {
      const d = f.data_lancamento || '';
      const key = `${d}||${f.projeto||''}`;
      if (!mapa[key]) mapa[key] = { data:d, colaborador:'', projeto:f.projeto||'', leads:0, perdidos:0, contratos:0, reunioes:0, receita:0, atividades:0 };
      mapa[key].receita += Number(f.valor || 0);
    });

    return Object.values(mapa).sort((a,b) => a.data.localeCompare(b.data));
  }, []);

  /* ── buscar contratos (casos) do período ── */
  const buscarCasos = useCallback(async (inicio, fim) => {
    const { data } = await supabase.from('casos')
      .select('id, nome_cliente, projeto_id, responsavel, tipos_acao, dt_contrato_assinado, dt_acao_protocolada, dt_sentenca, honorarios_exito, honorarios_sucumbencia, created_at, pendente_preenchimento')
      .gte('created_at', inicio + 'T00:00:00')
      .lte('created_at', fim + 'T23:59:59')
      .order('created_at', { ascending: false });
    return data || [];
  }, []);

  /* ── buscar tarefas do período ── */
  const buscarTarefas = useCallback(async (inicio, fim) => {
    // Busca tarefas agendadas no período, com dados do lead (responsavel e projeto)
    const { data } = await supabase
      .from('crm_tarefas')
      .select('id, titulo, concluida, data_hora, lead_id, crm_leads(responsavel, projeto)')
      .gte('data_hora', inicio + 'T00:00:00')
      .lte('data_hora', fim + 'T23:59:59')
      .order('data_hora', { ascending: false });
    return (data || []).map(t => ({
      ...t,
      responsavel: t.crm_leads?.responsavel || '',
      projeto:     t.crm_leads?.projeto || '',
      dia:         (t.data_hora||'').split('T')[0],
    }));
  }, []);

  /* ── efeito principal ── */
  useEffect(() => {
    async function carregar() {
      setCarregando(true);
      const inicio = tipoPeriodo === 'dia' ? diaEspec : dataInicio;
      const fim    = tipoPeriodo === 'dia' ? diaEspec : dataFim;
      const [ag, ag2, cs, tf] = await Promise.all([
        buscarAgregado(inicio, fim),
        comparar ? buscarAgregado(compInicio, compFim) : Promise.resolve([]),
        buscarCasos(inicio, fim),
        buscarTarefas(inicio, fim),
      ]);
      setLinhas(ag);
      setLinhasComp(ag2);
      setCasos(cs);
      setTarefasData(tf);
      setCarregando(false);
    }
    carregar();
  }, [tipoPeriodo, diaEspec, dataInicio, dataFim, comparar, compInicio, compFim, buscarAgregado, buscarCasos, buscarTarefas]);

  /* ── filtros aplicados ── */
  const filtrar = (arr) => arr.filter(d =>
    (!filtroColab || d.colaborador === filtroColab) &&
    (!filtroProj  || d.projeto === filtroProj)
  );
  const dados     = filtrar(linhas);
  const dadosComp = filtrar(linhasComp);

  const casosFiltrados = casos.filter(c =>
    (!filtroProj  || (projetosMap[c.projeto_id] || '') === filtroProj) &&
    (!filtroColab || c.responsavel === filtroColab)
  );

  const tarefasFiltradas = tarefasData.filter(t =>
    (!filtroProj  || t.projeto === filtroProj) &&
    (!filtroColab || t.responsavel === filtroColab)
  );

  /* ── totais ── */
  const tot = {
    leads:          somarCampo(dados, 'leads'),
    perdidos:       somarCampo(dados, 'perdidos'),
    reunioes:       somarCampo(dados, 'reunioes'),
    receita:        somarCampo(dados, 'receita'),
    atividades:     somarCampo(dados, 'atividades'),
    contratos:      casosFiltrados.length,
    honorarios:     casosFiltrados.reduce((a,c) => a + (Number(c.honorarios_exito)||0) + (Number(c.honorarios_sucumbencia)||0), 0),
    sentencas:      casosFiltrados.filter(c => c.dt_sentenca).length,
    protocolados:   casosFiltrados.filter(c => c.dt_acao_protocolada).length,
    tarefasCriadas: tarefasFiltradas.length,
    tarefasFeitas:  tarefasFiltradas.filter(t => t.concluida).length,
    tarefasPend:    tarefasFiltradas.filter(t => !t.concluida).length,
  };
  const totComp = comparar ? {
    leads:    somarCampo(dadosComp, 'leads'),
    perdidos: somarCampo(dadosComp, 'perdidos'),
    reunioes: somarCampo(dadosComp, 'reunioes'),
    receita:  somarCampo(dadosComp, 'receita'),
  } : {};

  const taxaConv        = tot.leads > 0 ? ((tot.reunioes / tot.leads) * 100).toFixed(1) : 0;
  const taxaFechamento  = tot.leads > 0 ? ((tot.contratos / tot.leads) * 100).toFixed(1) : 0;
  const taxaTarefas     = tot.tarefasCriadas > 0 ? ((tot.tarefasFeitas / tot.tarefasCriadas) * 100).toFixed(0) : 0;

  /* ── agrupamentos ── */
  const porDia = (() => {
    const mapa = {};
    dados.forEach(d => {
      if (!mapa[d.data]) mapa[d.data] = { dia: d.data.slice(8,10)+'/'+d.data.slice(5,7), leads:0, reunioes:0, receita:0, atividades:0, tarefas:0 };
      mapa[d.data].leads      += d.leads || 0;
      mapa[d.data].reunioes   += d.reunioes || 0;
      mapa[d.data].receita    += d.receita || 0;
      mapa[d.data].atividades += d.atividades || 0;
    });
    tarefasFiltradas.forEach(t => {
      const d = t.dia;
      if (!mapa[d]) mapa[d] = { dia: d.slice(8,10)+'/'+d.slice(5,7), leads:0, reunioes:0, receita:0, atividades:0, tarefas:0 };
      mapa[d].tarefas++;
    });
    return Object.values(mapa).sort((a,b) => {
      const [da, ma] = a.dia.split('/'); const [db, mb] = b.dia.split('/');
      return (ma+da).localeCompare(mb+db);
    });
  })();

  const porColab = (() => {
    const mapa = {};
    const novo = (k) => ({ nome:k, leads:0, perdidos:0, reunioes:0, receita:0, atividades:0, contratos:0, tarefasFeitas:0, tarefasPend:0, tarefasTotal:0 });
    dados.forEach(d => {
      const k = d.colaborador || 'Sem responsável';
      if (!mapa[k]) mapa[k] = novo(k);
      mapa[k].leads      += d.leads || 0;
      mapa[k].perdidos   += d.perdidos || 0;
      mapa[k].reunioes   += d.reunioes || 0;
      mapa[k].receita    += d.receita || 0;
      mapa[k].atividades += d.atividades || 0;
    });
    casosFiltrados.forEach(c => {
      const k = c.responsavel || 'Sem responsável';
      if (!mapa[k]) mapa[k] = novo(k);
      mapa[k].contratos++;
    });
    tarefasFiltradas.forEach(t => {
      const k = t.responsavel || 'Sem responsável';
      if (!mapa[k]) mapa[k] = novo(k);
      mapa[k].tarefasTotal++;
      if (t.concluida) mapa[k].tarefasFeitas++;
      else              mapa[k].tarefasPend++;
    });
    // score de produtividade: tarefas feitas×2 + reunioes×3 + leads + contratos×5
    Object.values(mapa).forEach(c => {
      c.score = c.tarefasFeitas*2 + c.reunioes*3 + c.leads + c.contratos*5;
    });
    return Object.values(mapa).sort((a,b) => b.score - a.score).filter(c => c.nome && c.nome !== 'Sem responsável');
  })();

  const porProj = (() => {
    const mapa = {};
    dados.forEach(d => {
      const k = d.projeto || 'Sem projeto';
      if (!mapa[k]) mapa[k] = { nome:k, leads:0, perdidos:0, reunioes:0, receita:0, atividades:0, contratos:0 };
      mapa[k].leads      += d.leads || 0;
      mapa[k].perdidos   += d.perdidos || 0;
      mapa[k].reunioes   += d.reunioes || 0;
      mapa[k].receita    += d.receita || 0;
      mapa[k].atividades += d.atividades || 0;
    });
    casosFiltrados.forEach(c => {
      const k = projetosMap[c.projeto_id] || 'Sem projeto';
      if (!mapa[k]) mapa[k] = { nome:k, leads:0, perdidos:0, reunioes:0, receita:0, atividades:0, contratos:0 };
      mapa[k].contratos++;
    });
    return Object.values(mapa).sort((a,b) => b.leads - a.leads).filter(p => p.nome && p.nome !== 'Sem projeto');
  })();

  /* ── snapshot de etapas ── */
  const etapasPorFunil = (() => {
    const mapa = {};
    (etapasSnap || []).forEach(l => {
      if (filtroProj && l.projeto !== filtroProj) return;
      const funil = l.funil_key || 'geral';
      if (!mapa[funil]) mapa[funil] = {};
      const etapa = l.perdido ? '(Perdidos)' : (l.etapa || 'Sem etapa');
      mapa[funil][etapa] = (mapa[funil][etapa] || 0) + 1;
    });
    return mapa;
  })();

  /* ── labels ── */
  const labelPeriodo = tipoPeriodo === 'dia' ? `${fmtData(diaEspec)}` : `${fmtData(dataInicio)} → ${fmtData(dataFim)}`;
  const labelComp    = `${fmtData(compInicio)} → ${fmtData(compFim)}`;
  const inicio       = tipoPeriodo === 'dia' ? diaEspec : dataInicio;
  const fim          = tipoPeriodo === 'dia' ? diaEspec : dataFim;

  const ABAS = [
    { key:'geral',          label:'Visão Geral' },
    { key:'contratos',      label:`Contratos (${tot.contratos})` },
    { key:'funil',          label:'Funil & Etapas' },
    { key:'colaboradores',  label:'Por Colaborador' },
    { key:'projetos',       label:'Por Projeto' },
    { key:'tabela',         label:'Tabela Completa' },
  ];

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Relatórios</h2>
        <div className="section-line" />
      </div>

      {/* ── Filtros ── */}
      <div className="filter-bar" style={{ marginBottom:16, alignItems:'center' }}>
        <select value={filtroProj} onChange={e => setFiltroProj(e.target.value)} className={`chip-select${filtroProj?' active':''}`}>
          <option value=''>Projeto</option>
          {projLista.map(p => <option key={p}>{p}</option>)}
        </select>
        <select value={filtroColab} onChange={e => setFiltroColab(e.target.value)} className={`chip-select${filtroColab?' active':''}`}>
          <option value=''>Colaborador</option>
          {colabLista.map(c => <option key={c}>{c}</option>)}
        </select>
        <div className="tabs-wrap">
          {[{key:'dia',label:'Dia'},{key:'mes',label:'30 dias'},{key:'personalizado',label:'Personalizado'}].map(t => (
            <button key={t.key} onClick={() => setTipoPeriodo(t.key)} className={`tab-btn${tipoPeriodo===t.key?' active':''}`}>{t.label}</button>
          ))}
        </div>
        {tipoPeriodo === 'dia' && (
          <input type="date" value={diaEspec} onChange={e => setDiaEspec(e.target.value)}
            style={{ padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:10, fontSize:13, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
        )}
        {tipoPeriodo === 'personalizado' && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
              style={{ padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:10, fontSize:12, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
            <span style={{ color:'#7B5EA7', fontSize:12 }}>→</span>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
              style={{ padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:10, fontSize:12, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
          </div>
        )}
        <div style={{ flex:1 }} />
        <button onClick={() => { setFiltroProj(''); setFiltroColab(''); setTipoPeriodo('mes'); setDataInicio(trintaDias); setDataFim(hoje); setComparar(false); }}
          style={{ padding:'7px 12px', border:'none', borderRadius:10, background:'#F3EBFF', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>✕ Limpar</button>
        <button onClick={() => exportarCSV([['Data','Colaborador','Projeto','Leads','Perdidos','Reuniões','Receita','Atividades'], ...dados.map(d=>[d.data,d.colaborador,d.projeto,d.leads,d.perdidos,d.reunioes,d.receita,d.atividades])], 'relatorio.csv')}
          style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.15)', padding:'8px 16px', borderRadius:10, cursor:'pointer', fontSize:12, color:'#7B5EA7', fontFamily:'Poppins,sans-serif', fontWeight:500 }}>
          Exportar CSV
        </button>
      </div>

      {/* Comparar */}
      <div style={{ marginBottom:16, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'#7B5EA7', fontWeight:600 }}>
          <input type="checkbox" checked={comparar} onChange={e => setComparar(e.target.checked)} />
          Comparar com período anterior
        </label>
        {comparar && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input type="date" value={compInicio} onChange={e => setCompInicio(e.target.value)}
              style={{ padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, fontSize:12, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
            <span style={{ color:'#7B5EA7', fontSize:12 }}>→</span>
            <input type="date" value={compFim} onChange={e => setCompFim(e.target.value)}
              style={{ padding:'7px 10px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:8, fontSize:12, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif' }} />
          </div>
        )}
        <span style={{ marginLeft:'auto', fontSize:12, color:'#7B5EA7', fontWeight:600, background:'#F3EBFF', padding:'4px 12px', borderRadius:20 }}>
          {carregando ? 'Carregando...' : `${labelPeriodo} · ${dados.length} registros`}
        </span>
      </div>

      {/* ── Tabs ── */}
      <div className="tabs-wrap" style={{ marginBottom:20 }}>
        {ABAS.map(a => (
          <button key={a.key} onClick={() => setAba(a.key)} className={`tab-btn${aba===a.key?' active':''}`}>{a.label}</button>
        ))}
      </div>

      {carregando && (
        <div style={{ padding:60, textAlign:'center', color:'#7B5EA7', fontSize:14 }}>Carregando dados...</div>
      )}

      {!carregando && (
        <>
          {/* ══════════ VISÃO GERAL ══════════ */}
          {aba === 'geral' && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
                <KpiGrad label='Leads entrados' valor={tot.leads} grad='linear-gradient(135deg,#3F0051,#7B2FBE)' icon='👤'
                  sub={comparar && totComp.leads !== undefined ? `ant: ${totComp.leads} (${tot.leads >= totComp.leads ? '+' : ''}${tot.leads - totComp.leads})` : labelPeriodo} />
                <KpiGrad label='Contratos gerados' valor={tot.contratos} grad='linear-gradient(135deg,#059669,#34d399)' icon='📋'
                  sub={`${tot.protocolados} protocolados · ${tot.sentencas} sentenças`} />
                <KpiGrad label='Perdidos' valor={tot.perdidos} grad='linear-gradient(135deg,#be2f2f,#f87171)' icon='✕'
                  sub={tot.leads > 0 ? `${((tot.perdidos/tot.leads)*100).toFixed(0)}% dos leads` : ''} />
                <KpiGrad label='Honorários potenciais' valor={fmtMoeda(tot.honorarios)} grad='linear-gradient(135deg,#b45309,#D4AF37)' icon='💰'
                  sub='Êxito + Sucumbência' />
                <KpiGrad label='Reuniões / Calls' valor={tot.reunioes} grad='linear-gradient(135deg,#1d4ed8,#60a5fa)' icon='💻'
                  sub={comparar && totComp.reunioes !== undefined ? `ant: ${totComp.reunioes}` : ''} />
                <KpiGrad label='Receita confirmada' valor={fmtMoeda(tot.receita)} grad='linear-gradient(135deg,#065f46,#34d399)' icon='✓'
                  sub={comparar && totComp.receita !== undefined ? `ant: ${fmtK(totComp.receita)}` : ''} />
                <KpiGrad label='Tarefas concluídas' valor={`${tot.tarefasFeitas}/${tot.tarefasCriadas}`} grad='linear-gradient(135deg,#0e7490,#22d3ee)' icon='✅'
                  sub={`${taxaTarefas}% de conclusão · ${tot.tarefasPend} pendentes`} />
                <KpiGrad label='Taxa leads→reunião' valor={`${taxaConv}%`} grad='linear-gradient(135deg,#6d28d9,#a78bfa)' icon='📊'
                  sub={`${tot.reunioes} de ${tot.leads} leads`} />
                <KpiGrad label='Taxa leads→contrato' valor={`${taxaFechamento}%`} grad='linear-gradient(135deg,#0e7490,#22d3ee)' icon='🎯'
                  sub={`${tot.contratos} de ${tot.leads} leads`} />
              </div>

              {comparar && dadosComp.length > 0 && (
                <div style={{ background:'white', borderRadius:16, padding:20, marginBottom:20, border:'1px solid rgba(107,33,168,0.1)' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#3F0051', marginBottom:14 }}>📊 Comparação de períodos</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                    {[
                      {label:'Leads',    cur:tot.leads,    comp:totComp.leads,    cor:'#6B2FA0'},
                      {label:'Perdidos', cur:tot.perdidos, comp:totComp.perdidos, cor:'#dc2626'},
                      {label:'Reuniões', cur:tot.reunioes, comp:totComp.reunioes, cor:'#2563eb'},
                      {label:'Receita',  cur:tot.receita,  comp:totComp.receita,  cor:'#059669', fmt:fmtMoeda},
                    ].map(m => {
                      const delta = m.cur - m.comp;
                      const pct = m.comp > 0 ? ((delta/m.comp)*100).toFixed(0) : '—';
                      return (
                        <div key={m.label} style={{ textAlign:'center', padding:14, background:'#FAF8FF', borderRadius:12 }}>
                          <div style={{ fontSize:10, color:'#7B5EA7', marginBottom:4, textTransform:'uppercase', fontWeight:600 }}>{m.label}</div>
                          <div style={{ fontSize:22, fontWeight:800, color:m.cor, fontFamily:'Playfair Display,serif' }}>{m.fmt ? m.fmt(m.cur) : m.cur}</div>
                          <div style={{ fontSize:11, color: delta>0?'#059669':delta<0?'#dc2626':'#7B5EA7', fontWeight:700, marginTop:4 }}>
                            {delta>0?'▲':'▼'} {Math.abs(delta)} ({pct !== '—' ? `${Math.abs(pct)}%` : '—'})
                          </div>
                          <div style={{ fontSize:10, color:'#9ca3af', marginTop:2 }}>ant: {m.fmt ? m.fmt(m.comp) : m.comp} · {labelComp}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {porDia.length > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14 }}>
                  {[
                    {campo:'leads',      cor:'#7B2FBE', label:'Leads entrados por dia'},
                    {campo:'tarefas',    cor:'#0e7490', label:'Tarefas agendadas por dia'},
                    {campo:'reunioes',   cor:'#2563eb', label:'Reuniões / Calls por dia'},
                    {campo:'receita',    cor:'#059669', label:'Receita confirmada por dia', fmt:fmtK},
                  ].map(g => (
                    <div key={g.campo} style={{ background:'white', borderRadius:16, padding:20, border:'1px solid rgba(107,33,168,0.08)', boxShadow:'0 2px 10px rgba(63,0,81,0.05)' }}>
                      <BarChart dados={porDia} campo={g.campo} cor={g.cor} label={g.label} fmt={g.fmt} />
                    </div>
                  ))}
                </div>
              )}

              {porDia.length === 0 && (
                <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:16, color:'#7B5EA7', border:'1px solid rgba(107,33,168,0.08)' }}>
                  <div style={{ fontSize:36, marginBottom:12, opacity:0.3 }}>✦</div>
                  <div style={{ fontFamily:'Playfair Display,serif', fontSize:15, color:'#3F0051', marginBottom:6 }}>Nenhum dado no período</div>
                  <div style={{ fontSize:12, color:'#C084FC' }}>Tente ampliar o intervalo de datas.</div>
                </div>
              )}
            </>
          )}

          {/* ══════════ CONTRATOS ══════════ */}
          {aba === 'contratos' && (
            <div>
              {/* KPIs de contratos */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
                <KpiGrad label='Contratos gerados' valor={tot.contratos} grad='linear-gradient(135deg,#3F0051,#7B2FBE)' icon='📋' sub={labelPeriodo} />
                <KpiGrad label='Ações protocoladas' valor={tot.protocolados} grad='linear-gradient(135deg,#1d4ed8,#60a5fa)' icon='⚖️' sub={`${tot.contratos>0?((tot.protocolados/tot.contratos)*100).toFixed(0):0}% dos contratos`} />
                <KpiGrad label='Sentenças obtidas' valor={tot.sentencas} grad='linear-gradient(135deg,#059669,#34d399)' icon='🏆' sub={`${tot.contratos>0?((tot.sentencas/tot.contratos)*100).toFixed(0):0}% dos contratos`} />
                <KpiGrad label='Honorários potenciais' valor={fmtMoeda(tot.honorarios)} grad='linear-gradient(135deg,#b45309,#D4AF37)' icon='💰' sub='Total êxito + sucumbência' />
              </div>

              {casosFiltrados.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:16, color:'#7B5EA7' }}>
                  <div style={{ fontFamily:'Playfair Display,serif', fontSize:15, color:'#3F0051', marginBottom:6 }}>Nenhum contrato no período</div>
                  <div style={{ fontSize:12, color:'#C084FC' }}>Contratos são gerados ao enviar um lead para contratos no CRM.</div>
                </div>
              ) : (
                <div style={{ background:'white', borderRadius:16, overflow:'hidden', border:'1px solid rgba(107,33,168,0.08)', boxShadow:'0 2px 10px rgba(63,0,81,0.05)' }}>
                  <div style={{ padding:'12px 18px', background:'linear-gradient(135deg,#FAFAFF,#F5EEFF)', borderBottom:'1px solid rgba(107,33,168,0.08)', display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr 1.2fr', gap:8 }}>
                    {['Cliente','Projeto','Responsável','Contrato','Protocolo','Sentença','Honorários'].map(h => (
                      <div key={h} style={{ fontSize:10, fontWeight:700, color:'#7B2FBE', textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</div>
                    ))}
                  </div>
                  {casosFiltrados.map((c, i) => {
                    const honTotal = (Number(c.honorarios_exito)||0) + (Number(c.honorarios_sucumbencia)||0);
                    const progresso = [c.dt_contrato_assinado, c.dt_acao_protocolada, c.dt_sentenca].filter(Boolean).length;
                    const cores = ['#dc2626','#d97706','#059669'];
                    return (
                      <div key={c.id} style={{ padding:'13px 18px', borderBottom:'1px solid rgba(107,33,168,0.05)', background: i%2===0?'transparent':'#FAFAFF', display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr 1.2fr', gap:8, alignItems:'center' }}>
                        <div>
                          <div style={{ fontWeight:600, color:'#1E0A3C', fontSize:13 }}>{c.nome_cliente}</div>
                          <div style={{ fontSize:10, color:'#7B5EA7', marginTop:2 }}>
                            {(c.tipos_acao||[]).slice(0,2).join(', ')}
                            {c.pendente_preenchimento && <span style={{ marginLeft:6, background:'#FEF3C7', color:'#92400e', padding:'1px 6px', borderRadius:10, fontSize:9, fontWeight:700 }}>Pendente</span>}
                          </div>
                        </div>
                        <div style={{ fontSize:12, color:'#7B5EA7' }}>{projetosMap[c.projeto_id] || '—'}</div>
                        <div style={{ fontSize:12, color:'#1E0A3C', fontWeight:500 }}>{c.responsavel || '—'}</div>
                        <div style={{ fontSize:11 }}>
                          {c.dt_contrato_assinado
                            ? <span style={{ color:'#059669', fontWeight:600 }}>✓ {fmtData(c.dt_contrato_assinado)}</span>
                            : <span style={{ color:'#dc2626' }}>—</span>}
                        </div>
                        <div style={{ fontSize:11 }}>
                          {c.dt_acao_protocolada
                            ? <span style={{ color:'#059669', fontWeight:600 }}>✓ {fmtData(c.dt_acao_protocolada)}</span>
                            : <span style={{ color:'#dc2626' }}>—</span>}
                        </div>
                        <div style={{ fontSize:11 }}>
                          {c.dt_sentenca
                            ? <span style={{ color:'#059669', fontWeight:600 }}>✓ {fmtData(c.dt_sentenca)}</span>
                            : <span style={{ color:'#dc2626' }}>—</span>}
                        </div>
                        <div style={{ fontSize:12, fontWeight:700, color: honTotal > 0 ? '#b45309' : '#9ca3af' }}>
                          {honTotal > 0 ? fmtMoeda(honTotal) : '—'}
                          <div style={{ display:'flex', gap:2, marginTop:4 }}>
                            {[0,1,2].map(j => (
                              <div key={j} style={{ height:4, flex:1, borderRadius:4, background: j < progresso ? cores[progresso-1] : '#e5e7eb' }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ padding:'10px 18px', background:'linear-gradient(135deg,#FAFAFF,#F5EEFF)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:12, color:'#7B5EA7', fontWeight:600 }}>{casosFiltrados.length} contrato(s)</span>
                    <span style={{ fontSize:13, fontWeight:800, color:'#b45309' }}>Total honorários: {fmtMoeda(tot.honorarios)}</span>
                    <button onClick={() => exportarCSV(
                      [['Cliente','Projeto','Responsável','Tipos de ação','Contrato assinado','Ação protocolada','Sentença','Hon. Êxito','Hon. Sucumbência','Total'],
                       ...casosFiltrados.map(c => [c.nome_cliente, projetosMap[c.projeto_id]||'', c.responsavel||'', (c.tipos_acao||[]).join('|'), c.dt_contrato_assinado||'', c.dt_acao_protocolada||'', c.dt_sentenca||'', c.honorarios_exito||0, c.honorarios_sucumbencia||0, (Number(c.honorarios_exito)||0)+(Number(c.honorarios_sucumbencia)||0)])],
                      'contratos.csv'
                    )} style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.15)', padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12, color:'#7B5EA7', fontFamily:'Poppins,sans-serif' }}>
                      Exportar CSV
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════ FUNIL & ETAPAS ══════════ */}
          {aba === 'funil' && (
            <div>
              <div style={{ marginBottom:8, fontSize:12, color:'#7B5EA7' }}>Snapshot atual — quantos leads estão em cada etapa agora{filtroProj ? ` (${filtroProj})` : ''}:</div>
              {Object.entries(etapasPorFunil).map(([funil, etapas]) => {
                const totalFunil = Object.values(etapas).reduce((a,b) => a+b, 0);
                const maxEtapa   = Math.max(...Object.values(etapas));
                return (
                  <div key={funil} style={{ background:'white', borderRadius:16, padding:20, marginBottom:14, border:'1px solid rgba(107,33,168,0.08)', boxShadow:'0 2px 10px rgba(63,0,81,0.05)' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                      <div style={{ fontFamily:'Playfair Display,serif', fontSize:15, color:'#3F0051', fontWeight:600 }}>{funil}</div>
                      <span style={{ background:'#F3EBFF', color:'#7B2FBE', padding:'3px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>{totalFunil} leads</span>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {Object.entries(etapas)
                        .sort((a,b) => b[1]-a[1])
                        .map(([etapa, qtd]) => (
                          <div key={etapa} style={{ display:'grid', gridTemplateColumns:'160px 1fr 40px', gap:10, alignItems:'center' }}>
                            <div style={{ fontSize:12, color: etapa==='(Perdidos)'?'#dc2626':'#1E0A3C', fontWeight: etapa==='(Perdidos)'?700:400, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{etapa}</div>
                            <MiniBar valor={qtd} max={maxEtapa} cor={etapa==='(Perdidos)'?'#dc2626':'#7B2FBE'} />
                            <div style={{ fontSize:11, color:'#7B5EA7', textAlign:'right' }}>{totalFunil>0?`${((qtd/totalFunil)*100).toFixed(0)}%`:''}</div>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
              {Object.keys(etapasPorFunil).length === 0 && (
                <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:16, color:'#7B5EA7' }}>Nenhum lead cadastrado ainda.</div>
              )}
            </div>
          )}

          {/* ══════════ POR COLABORADOR ══════════ */}
          {aba === 'colaboradores' && (
            <div>
              {porColab.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:16, color:'#7B5EA7' }}>Nenhum dado no período.</div>
              ) : (
                <>
                  {/* Cards de ranking */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:18 }}>
                    {porColab.slice(0,3).map((c, i) => {
                      const medals = ['🥇','🥈','🥉'];
                      const taxaTf = c.tarefasTotal > 0 ? ((c.tarefasFeitas/c.tarefasTotal)*100).toFixed(0) : 0;
                      return (
                        <div key={i} style={{ background:'white', borderRadius:16, padding:20, border: i===0 ? '2px solid #D4AF37' : '1px solid rgba(107,33,168,0.1)', boxShadow: i===0 ? '0 6px 24px rgba(212,175,55,0.2)' : '0 2px 10px rgba(63,0,81,0.05)', position:'relative' }}>
                          <div style={{ position:'absolute', top:14, right:16, fontSize:22 }}>{medals[i]}</div>
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                            <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#3F0051,#7B2FBE)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'white' }}>
                              {c.nome.split(' ').map(p=>p[0]).slice(0,2).join('')}
                            </div>
                            <div>
                              <div style={{ fontWeight:700, color:'#1E0A3C', fontSize:13 }}>{c.nome.split(' ')[0]}</div>
                              <div style={{ fontSize:10, color:'#7B5EA7' }}>Score: {c.score} pts</div>
                            </div>
                          </div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                            {[
                              {label:'Leads',     valor:c.leads,        cor:'#7B2FBE', bg:'#F3EBFF'},
                              {label:'Contratos', valor:c.contratos,    cor:'#059669', bg:'#dcfce7'},
                              {label:'Reuniões',  valor:c.reunioes,     cor:'#2563eb', bg:'#dbeafe'},
                              {label:'Tarefas ✓', valor:`${c.tarefasFeitas}/${c.tarefasTotal}`, cor:'#0e7490', bg:'#cffafe'},
                            ].map(m => (
                              <div key={m.label} style={{ background:m.bg, borderRadius:10, padding:'8px 10px', textAlign:'center' }}>
                                <div style={{ fontSize:16, fontWeight:800, color:m.cor, fontFamily:'Playfair Display,serif' }}>{m.valor}</div>
                                <div style={{ fontSize:9, color:m.cor, fontWeight:600, textTransform:'uppercase', opacity:0.8 }}>{m.label}</div>
                              </div>
                            ))}
                          </div>
                          {c.tarefasTotal > 0 && (
                            <div style={{ marginTop:12 }}>
                              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                                <span style={{ fontSize:10, color:'#7B5EA7', fontWeight:600 }}>Taxa de conclusão de tarefas</span>
                                <span style={{ fontSize:10, fontWeight:700, color: Number(taxaTf)>=75?'#059669':Number(taxaTf)>=50?'#d97706':'#dc2626' }}>{taxaTf}%</span>
                              </div>
                              <div style={{ height:6, background:'rgba(107,33,168,0.08)', borderRadius:10, overflow:'hidden' }}>
                                <div style={{ width:`${taxaTf}%`, height:'100%', background: Number(taxaTf)>=75?'#059669':Number(taxaTf)>=50?'#d97706':'#dc2626', borderRadius:10, transition:'width 0.5s' }} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Tabela completa */}
                  <div style={{ background:'white', borderRadius:16, overflow:'hidden', border:'1px solid rgba(107,33,168,0.08)', marginBottom:16 }}>
                    <div style={{ padding:'12px 18px', background:'linear-gradient(135deg,#FAFAFF,#F5EEFF)', borderBottom:'1px solid rgba(107,33,168,0.08)', display:'grid', gridTemplateColumns:'2fr 0.8fr 0.8fr 0.8fr 0.8fr 1.2fr 1.2fr 0.8fr 1fr', gap:8 }}>
                      {['Colaborador','Leads','Perd.','Contratos','Reuniões','Tarefas ✓','Tarefas ⏳','Score','Receita'].map(h => (
                        <div key={h} style={{ fontSize:10, fontWeight:700, color:'#7B2FBE', textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</div>
                      ))}
                    </div>
                    {porColab.map((c, i) => {
                      const taxaTf = c.tarefasTotal > 0 ? Number(((c.tarefasFeitas/c.tarefasTotal)*100).toFixed(0)) : 0;
                      return (
                        <div key={i} style={{ padding:'12px 18px', borderBottom:'1px solid rgba(107,33,168,0.05)', background:i%2===0?'transparent':'#FAFAFF', display:'grid', gridTemplateColumns:'2fr 0.8fr 0.8fr 0.8fr 0.8fr 1.2fr 1.2fr 0.8fr 1fr', gap:8, alignItems:'center' }}>
                          <div style={{ fontWeight:700, color:'#1E0A3C', display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#3F0051,#7B2FBE)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'white', flexShrink:0 }}>
                              {c.nome.split(' ').map(p=>p[0]).slice(0,2).join('')}
                            </div>
                            <span style={{ fontSize:12 }}>{c.nome}</span>
                          </div>
                          <div style={{ color:'#7B2FBE', fontWeight:700 }}>{c.leads}</div>
                          <div style={{ color:'#dc2626', fontWeight:600 }}>{c.perdidos}</div>
                          <div style={{ color:'#059669', fontWeight:700 }}>{c.contratos}</div>
                          <div style={{ color:'#2563eb', fontWeight:600 }}>{c.reunioes}</div>
                          <div>
                            <span style={{ fontWeight:700, color:'#059669', background:'#dcfce7', padding:'2px 8px', borderRadius:20, fontSize:11 }}>✓ {c.tarefasFeitas}</span>
                          </div>
                          <div>
                            {c.tarefasPend > 0
                              ? <span style={{ fontWeight:700, color:'#d97706', background:'#fef3c7', padding:'2px 8px', borderRadius:20, fontSize:11 }}>⏳ {c.tarefasPend}</span>
                              : <span style={{ color:'#9ca3af', fontSize:11 }}>—</span>}
                          </div>
                          <div>
                            <span style={{ fontWeight:800, color: i===0?'#b45309':'#7B2FBE', fontSize:12 }}>{c.score}</span>
                          </div>
                          <div style={{ color:'#b45309', fontWeight:700, fontSize:11 }}>{fmtMoeda(c.receita)}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Gráficos */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
                    <div style={{ background:'white', borderRadius:16, padding:20, border:'1px solid rgba(107,33,168,0.08)' }}>
                      <BarChart dados={porColab} campo="leads" cor="#7B2FBE" label="Leads por colaborador" />
                    </div>
                    <div style={{ background:'white', borderRadius:16, padding:20, border:'1px solid rgba(107,33,168,0.08)' }}>
                      <BarChart dados={porColab} campo="tarefasFeitas" cor="#0e7490" label="Tarefas concluídas" />
                    </div>
                    <div style={{ background:'white', borderRadius:16, padding:20, border:'1px solid rgba(107,33,168,0.08)' }}>
                      <BarChart dados={porColab} campo="contratos" cor="#059669" label="Contratos fechados" />
                    </div>
                  </div>

                  {/* Botão exportar */}
                  <div style={{ marginTop:14, display:'flex', justifyContent:'flex-end' }}>
                    <button onClick={() => exportarCSV(
                      [['Colaborador','Leads','Perdidos','Contratos','Reuniões','Tarefas feitas','Tarefas pendentes','Score','Receita'],
                       ...porColab.map(c=>[c.nome,c.leads,c.perdidos,c.contratos,c.reunioes,c.tarefasFeitas,c.tarefasPend,c.score,c.receita])],
                      'colaboradores.csv'
                    )} style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.15)', padding:'8px 18px', borderRadius:10, cursor:'pointer', fontSize:12, color:'#7B5EA7', fontFamily:'Poppins,sans-serif', fontWeight:600 }}>
                      Exportar CSV
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══════════ POR PROJETO ══════════ */}
          {aba === 'projetos' && (
            <div>
              {porProj.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:16, color:'#7B5EA7' }}>Nenhum dado no período.</div>
              ) : (
                <>
                  <div style={{ background:'white', borderRadius:16, overflow:'hidden', border:'1px solid rgba(107,33,168,0.08)', marginBottom:16 }}>
                    <div style={{ padding:'12px 18px', background:'linear-gradient(135deg,#FAFAFF,#F5EEFF)', borderBottom:'1px solid rgba(107,33,168,0.08)', display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr', gap:8 }}>
                      {['Projeto','Leads','Perdidos','Contratos','Reuniões','Receita'].map(h => (
                        <div key={h} style={{ fontSize:10, fontWeight:700, color:'#7B2FBE', textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</div>
                      ))}
                    </div>
                    {porProj.map((p, i) => (
                      <div key={i} style={{ padding:'13px 18px', borderBottom:'1px solid rgba(107,33,168,0.05)', background:i%2===0?'transparent':'#FAFAFF', display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr', gap:8, alignItems:'center' }}>
                        <div style={{ fontWeight:700, color:'#1E0A3C', fontSize:13 }}>{p.nome}</div>
                        <div style={{ color:'#7B2FBE', fontWeight:700 }}>{p.leads}</div>
                        <div style={{ color:'#dc2626', fontWeight:600 }}>{p.perdidos}</div>
                        <div style={{ color:'#059669', fontWeight:700 }}>{p.contratos}</div>
                        <div style={{ color:'#2563eb', fontWeight:600 }}>{p.reunioes}</div>
                        <div style={{ color:'#b45309', fontWeight:700, fontSize:12 }}>{fmtMoeda(p.receita)}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                    <div style={{ background:'white', borderRadius:16, padding:20, border:'1px solid rgba(107,33,168,0.08)' }}>
                      <BarChart dados={porProj} campo="leads" cor="#7B2FBE" label="Leads por projeto" />
                    </div>
                    <div style={{ background:'white', borderRadius:16, padding:20, border:'1px solid rgba(107,33,168,0.08)' }}>
                      <BarChart dados={porProj} campo="contratos" cor="#059669" label="Contratos por projeto" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══════════ TABELA COMPLETA ══════════ */}
          {aba === 'tabela' && (
            <div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                <button onClick={() => exportarCSV(
                  [['Data','Colaborador','Projeto','Leads','Perdidos','Reuniões','Receita','Atividades'],
                   ...dados.map(d=>[d.data,d.colaborador||'',d.projeto||'',d.leads||0,d.perdidos||0,d.reunioes||0,d.receita||0,d.atividades||0])],
                  'relatorio_completo.csv'
                )} style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'9px 22px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)' }}>
                  Exportar CSV
                </button>
              </div>
              <div style={{ background:'white', borderRadius:16, overflow:'hidden', border:'1px solid rgba(107,33,168,0.08)' }}>
                <div style={{ padding:'12px 18px', background:'linear-gradient(135deg,#FAFAFF,#F5EEFF)', borderBottom:'1px solid rgba(107,33,168,0.08)', display:'grid', gridTemplateColumns:'1fr 1.5fr 1.5fr 0.7fr 0.7fr 0.7fr 1fr 0.7fr', gap:8 }}>
                  {['Data','Colaborador','Projeto','Leads','Perdidos','Reuniões','Receita','Atividades'].map(h => (
                    <div key={h} style={{ fontSize:10, fontWeight:700, color:'#7B2FBE', textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</div>
                  ))}
                </div>
                <div style={{ maxHeight:500, overflowY:'auto' }}>
                  {dados.map((d, i) => (
                    <div key={i} style={{ padding:'11px 18px', borderBottom:'1px solid rgba(107,33,168,0.04)', background:i%2===0?'transparent':'#FAFAFF', display:'grid', gridTemplateColumns:'1fr 1.5fr 1.5fr 0.7fr 0.7fr 0.7fr 1fr 0.7fr', gap:8, alignItems:'center' }}>
                      <div style={{ fontSize:12, color:'#7B5EA7' }}>{fmtData(d.data)}</div>
                      <div style={{ fontSize:12, fontWeight:600, color:'#1E0A3C' }}>{d.colaborador||'—'}</div>
                      <div style={{ fontSize:12, color:'#7B5EA7' }}>{d.projeto||'—'}</div>
                      <div style={{ color:'#7B2FBE', fontWeight:700 }}>{d.leads||0}</div>
                      <div style={{ color:'#dc2626', fontWeight:600 }}>{d.perdidos||0}</div>
                      <div style={{ color:'#2563eb', fontWeight:600 }}>{d.reunioes||0}</div>
                      <div style={{ color:'#b45309', fontWeight:700, fontSize:11 }}>{fmtMoeda(d.receita||0)}</div>
                      <div style={{ color:'#059669', fontWeight:600 }}>{d.atividades||0}</div>
                    </div>
                  ))}
                  {dados.length === 0 && (
                    <div style={{ padding:40, textAlign:'center', color:'#7B5EA7' }}>Nenhum dado no período selecionado.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
